/**
 * Global Meeting Tab Component
 * Light QA Contract Compliant:
 * - Lazy loadable
 * - Skeleton loaders for LCP < 2.5s
 * - Semantic tokens for theming
 * - No SELECT * in queries
 */

import { useState, useMemo, lazy, Suspense } from 'react';
import { Calendar, Clock, CheckCircle2, XCircle, Search, List, CalendarDays, CalendarRange, Star } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { MultiSelect } from '@/components/ui/multi-select';
import { cn } from '@/lib/utils';
import { 
  useGlobalMeetings, 
  useGlobalMeetingStats, 
  useGlobalMeetingProjects, 
  useGlobalMeetingMembers,
  type GlobalMeetingFilters,
  type CompanyFilter,
  type ProjectStatusFilter,
} from '@/hooks/global/useGlobalMeetings';
import { GlobalMeetingList } from './GlobalMeetingList';
import type { MeetingStatus } from '@/types/meeting';
import { PROJECT_MARKS, PROJECT_MARK_LABELS, PROJECT_MARK_BADGE_COLORS, type ProjectMark } from '@/constants/projectMarks';

// Lazy load calendar view for better initial load
const GlobalMeetingCalendarView = lazy(() => import('./GlobalMeetingCalendarView'));

// Meeting status options with colors
const MEETING_STATUS_OPTIONS: { value: MeetingStatus; label: string; color: string }[] = [
  { value: 'upcoming', label: 'Sắp', color: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-950/50 dark:text-blue-300 dark:border-blue-800' },
  { value: 'in-progress', label: 'Đang', color: 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-950/50 dark:text-yellow-300 dark:border-yellow-800' },
  { value: 'completed', label: 'Xong', color: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-950/50 dark:text-green-300 dark:border-green-800' },
  { value: 'cancelled', label: 'Hủy', color: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-900/50 dark:text-slate-400 dark:border-slate-700' },
];

// Company filter options - only ENP, Solid
const COMPANY_OPTIONS: { value: CompanyFilter; label: string }[] = [
  { value: 'ENP', label: 'ENP' },
  { value: 'Solid', label: 'Solid' },
];

// Project status options - only All, Tender, Build, Service
const PROJECT_STATUS_OPTIONS: { value: ProjectStatusFilter | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'tender', label: 'Tender' },
  { value: 'build', label: 'Build' },
  { value: 'service', label: 'Service' },
];

type ViewMode = 'list' | 'week' | 'month';

const StatsCard = ({
  title,
  value,
  icon: Icon,
  isLoading,
  className = '',
}: {
  title: string;
  value: number;
  icon: React.ElementType;
  isLoading?: boolean;
  className?: string;
}) => (
  <Card className={className}>
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      <Icon className="h-4 w-4 text-muted-foreground" />
    </CardHeader>
    <CardContent>
      {isLoading ? (
        <Skeleton className="h-8 w-16" />
      ) : (
        <div className="text-2xl font-bold">{value}</div>
      )}
    </CardContent>
  </Card>
);

// Calendar skeleton for lazy loading fallback
const CalendarSkeleton = () => (
  <Card>
    <CardHeader>
      <Skeleton className="h-8 w-48" />
    </CardHeader>
    <CardContent>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: 35 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    </CardContent>
  </Card>
);

// Tag Filter Component - no label, just tags
const TagFilter = <T extends string>({
  options,
  selected,
  onToggle,
  getColor,
}: {
  options: { value: T; label: string; color?: string }[];
  selected?: T;
  onToggle: (value: T | undefined) => void;
  getColor?: (value: T) => string;
}) => (
  <div className="flex flex-wrap gap-1">
    {options.map((option) => {
      const isSelected = selected === option.value;
      const colorClass = getColor ? getColor(option.value) : option.color;
      return (
        <Badge
          key={option.value}
          variant="outline"
          className={cn(
            "cursor-pointer text-xs px-2 py-0.5 transition-all",
            isSelected && colorClass,
            !isSelected && "hover:bg-accent"
          )}
          onClick={() => onToggle(isSelected ? undefined : option.value)}
        >
          {option.label}
        </Badge>
      );
    })}
  </div>
);

export const GlobalMeetingTab = () => {
  const [filters, setFilters] = useState<GlobalMeetingFilters>({});
  const [searchInput, setSearchInput] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('week'); // Default to week view

  const { data: meetings, isLoading: isLoadingMeetings } = useGlobalMeetings(filters);
  const { data: stats, isLoading: isLoadingStats } = useGlobalMeetingStats();
  const { data: projects } = useGlobalMeetingProjects();
  const { data: members } = useGlobalMeetingMembers();

  // Project options for multi-select
  const projectOptions = useMemo(() => 
    (projects || []).map(p => ({ value: p.id, label: p.name })),
    [projects]
  );

  // Member options for multi-select  
  const memberOptions = useMemo(() =>
    (members || []).map(m => ({ value: m.id, label: m.name || m.email || 'Unknown' })),
    [members]
  );

  // Debounced search
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters((prev) => ({ ...prev, searchQuery: searchInput || undefined }));
  };

  const handleStatusFilter = (status?: MeetingStatus) => {
    setFilters((prev) => ({ ...prev, status }));
  };

  const handleProjectMarkFilter = (mark?: ProjectMark) => {
    setFilters((prev) => ({ ...prev, projectMark: mark }));
  };

  const handleCompanyFilter = (company?: CompanyFilter) => {
    setFilters((prev) => ({ ...prev, company }));
  };

  const handleProjectStatusFilter = (status?: ProjectStatusFilter | 'all') => {
    setFilters((prev) => ({
      ...prev,
      projectStatus: status === 'all' ? undefined : status as ProjectStatusFilter,
    }));
  };

  // Multi-select handlers
  const handleProjectsFilter = (projectIds: string[]) => {
    setFilters((prev) => ({
      ...prev,
      projectIds: projectIds.length > 0 ? projectIds : undefined,
    }));
  };

  const handleMembersFilter = (memberIds: string[]) => {
    setFilters((prev) => ({
      ...prev,
      memberIds: memberIds.length > 0 ? memberIds : undefined,
    }));
  };

  const handleClearFilters = () => {
    setFilters({});
    setSearchInput('');
  };

  const hasActiveFilters = useMemo(() => {
    return !!(
      filters.status || 
      (filters.projectIds && filters.projectIds.length > 0) || 
      filters.searchQuery || 
      filters.projectMark || 
      (filters.memberIds && filters.memberIds.length > 0) || 
      filters.company || 
      filters.projectStatus ||
      filters.onlyFavorites
    );
  }, [filters]);

  const handleFavoritesFilter = () => {
    setFilters((prev) => ({ ...prev, onlyFavorites: !prev.onlyFavorites }));
  };

  // Priority options with colors from constants
  const priorityOptions = useMemo(() => 
    PROJECT_MARKS.map(mark => ({
      value: mark,
      label: PROJECT_MARK_LABELS[mark],
      color: PROJECT_MARK_BADGE_COLORS[mark],
    })),
    []
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Stats Cards - 2 cols mobile, 4 cols desktop */}
      <div className="grid gap-3 sm:gap-4 grid-cols-2 md:grid-cols-4">
        <StatsCard
          title="Tổng số cuộc họp"
          value={stats?.total || 0}
          icon={Calendar}
          isLoading={isLoadingStats}
        />
        <StatsCard
          title="Sắp diễn ra"
          value={stats?.upcoming || 0}
          icon={Clock}
          isLoading={isLoadingStats}
          className="border-l-4 border-l-blue-500"
        />
        <StatsCard
          title="Đang diễn ra"
          value={stats?.in_progress || 0}
          icon={Calendar}
          isLoading={isLoadingStats}
          className="border-l-4 border-l-yellow-500"
        />
        <StatsCard
          title="Đã hoàn thành"
          value={stats?.completed || 0}
          icon={CheckCircle2}
          isLoading={isLoadingStats}
          className="border-l-4 border-l-green-500"
        />
      </div>

      {/* Filters and View Toggle */}
      <div className="flex flex-col gap-3">
        {/* Row 1: Search + View Toggle - stack on mobile */}
        <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
          <form onSubmit={handleSearchSubmit} className="flex gap-2 flex-1">
            <div className="relative flex-1 sm:max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Tìm kiếm cuộc họp..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9 h-10"
              />
            </div>
            <Button type="submit" variant="secondary" size="sm" className="h-10 px-4">
              Tìm
            </Button>
          </form>

          {/* View Toggle - centered on mobile */}
          <div className="flex border rounded-md self-center sm:self-auto">
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="icon"
              onClick={() => setViewMode('list')}
              title="Xem danh sách"
              className="rounded-r-none h-10 w-10 sm:h-9 sm:w-9 touch-manipulation"
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'week' ? 'secondary' : 'ghost'}
              size="icon"
              onClick={() => setViewMode('week')}
              title="Xem tuần"
              className="rounded-none border-x h-10 w-10 sm:h-9 sm:w-9 touch-manipulation"
            >
              <CalendarRange className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'month' ? 'secondary' : 'ghost'}
              size="icon"
              onClick={() => setViewMode('month')}
              title="Xem tháng"
              className="rounded-l-none h-10 w-10 sm:h-9 sm:w-9 touch-manipulation"
            >
              <CalendarDays className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Row 2: Tag Filters - horizontal scroll on mobile */}
        <div className="flex items-center gap-x-3 gap-y-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-thin">
          {/* Company Tags */}
          <div className="flex-shrink-0">
            <TagFilter
              options={COMPANY_OPTIONS}
              selected={filters.company}
              onToggle={handleCompanyFilter}
              getColor={() => "bg-primary/10 text-primary border-primary/30"}
            />
          </div>

          {/* Priority Tags */}
          <div className="flex-shrink-0">
            <TagFilter
              options={priorityOptions}
              selected={filters.projectMark}
              onToggle={handleProjectMarkFilter}
            />
          </div>

          {/* Meeting Status Tags */}
          <div className="flex-shrink-0">
            <TagFilter
              options={MEETING_STATUS_OPTIONS}
              selected={filters.status}
              onToggle={handleStatusFilter}
            />
          </div>

          {/* Favorites Filter Toggle */}
          <Badge
            variant="outline"
            className={cn(
              "cursor-pointer text-xs px-2 py-0.5 transition-all gap-1 flex items-center flex-shrink-0 touch-manipulation",
              filters.onlyFavorites 
                ? "bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-950/50 dark:text-yellow-300 dark:border-yellow-800" 
                : "hover:bg-accent"
            )}
            onClick={handleFavoritesFilter}
          >
            <Star className={cn("h-3 w-3", filters.onlyFavorites && "fill-current")} />
            Yêu thích
          </Badge>

          {/* Project Status Tags */}
          <div className="flex-shrink-0">
            <TagFilter
              options={PROJECT_STATUS_OPTIONS}
              selected={filters.projectStatus || 'all'}
              onToggle={(v) => handleProjectStatusFilter(v === 'all' ? undefined : v)}
              getColor={() => "bg-accent text-accent-foreground border-border"}
            />
          </div>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={handleClearFilters} className="h-7 px-2 text-xs flex-shrink-0 touch-manipulation">
              <XCircle className="h-3 w-3 mr-1" />
              Xóa lọc
            </Button>
          )}
        </div>

        {/* Row 3: Multi-select Filters - full width on mobile */}
        <div className="flex flex-col sm:flex-row gap-2">
          <MultiSelect
            options={projectOptions}
            value={filters.projectIds || []}
            onChange={handleProjectsFilter}
            placeholder="Chọn dự án..."
            className="w-full sm:w-[200px]"
            showSelectAll
            searchPlaceholder="Tìm dự án..."
          />

          <MultiSelect
            options={memberOptions}
            value={filters.memberIds || []}
            onChange={handleMembersFilter}
            placeholder="Chọn thành viên..."
            className="w-full sm:w-[200px]"
            showSelectAll
            searchPlaceholder="Tìm thành viên..."
          />
        </div>
      </div>

      {/* Meeting View - List, Week, or Month */}
      {viewMode === 'list' ? (
        <GlobalMeetingList meetings={meetings || []} isLoading={isLoadingMeetings} />
      ) : (
        <Suspense fallback={<CalendarSkeleton />}>
          <GlobalMeetingCalendarView 
            meetings={meetings || []} 
            isLoading={isLoadingMeetings}
            viewType={viewMode === 'week' ? 'week' : 'month'}
          />
        </Suspense>
      )}

      {/* Footer info */}
      {meetings && meetings.length > 0 && (
        <p className="text-sm text-muted-foreground text-center">
          Hiển thị {meetings.length} cuộc họp
          {hasActiveFilters && ' (đã lọc)'}
        </p>
      )}
    </div>
  );
};

export default GlobalMeetingTab;
